// @givmo/assistant-panel — the panel: a full-height companion beside the page, not on top of it.
//
// ══ NON-MODAL, AND PAIRED WITH `disablePointerDismissal` ═══════════════════════════════════════
//
// A working companion panel wants the page behind it to stay usable — scrollable, clickable, and
// above all able to receive an inserted draft while the panel is still open. So `modal={false}`, and
// the Sheet draws no scrim (its scrim is derived from modality precisely so a non-modal panel can
// never look blocking while behaving otherwise).
//
// ⚠ `disablePointerDismissal` is REQUIRED alongside it, not optional. Base UI closes a non-modal
// dialog when focus leaves it, so without the pair, clicking into the very field the panel just wrote
// to would close the panel — the interaction the non-modal mode exists to permit would be the one
// that breaks it.
//
// ══ THE HEADER IS THE TITLE AND THE CLOSE, AND NOTHING ELSE ════════════════════════════════════
//
// There is deliberately no new-conversation control. One thread persists (D-EP-40), and a
// clear-thread button would contradict the ruling — so its absence is a decision, not an omission.
//
// ══ WHEN THE PANEL REFUSES, AND WHEN IT DOES NOT ═══════════════════════════════════════════════
//
// It closes the composer for two states and only two: the organization has no standing acceptance,
// or this environment serves no instrument to accept. A consent read that FAILED closes nothing —
// that read never learned anything, and a client that blocked on it would tell a person the
// organization has not accepted when it may well have. The server's `428` is the authority; this
// client only anticipates it.
//
// ⚠ THE ACCEPTANCE CARD REPLACES THE PANEL; IT DOES NOT SIT ON TOP OF IT. Someone who can bind the
// organization, opening an assistant the organization has not turned on, is being asked ONE question
// — and a panel they cannot use behind a card asking it is noise. It is also the only composition
// with an unambiguous stacking: the card's own scrim is `z-40` and the panel is `z-50`, so drawing
// both would leave the panel bright and un-dimmed above the scrim meant to cover it. "Not now"
// closes everything and leaves the portal exactly as it was.
//
// ══ WHERE THE PANEL SITS ═══════════════════════════════════════════════════════════════════════
//
// Beside the page on a wide screen (docked, `docked`), or the right-hand sheet over it on a narrow
// one — both drawn by `AssistantPanelFrame`, which says why. A console's `ConsoleAssistant` decides
// which, and whether the panel is open, from its scope registry (`assistant-scopes.ts`).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AssistantAnalyticsEmit, BlockRenderContext } from '@givmo/ai';
import { ASSISTANT_EVENTS } from '@givmo/ai';
import { EmptyState, Icon, Sheet, testIdProp } from '@givmo/design-system';
import { Sparkles } from 'lucide-react';
import { AssistantPanelFrame } from './assistant-panel-frame';
import {
  AssistantBlockedLine,
  AssistantConsentCard,
  AssistantFirstUseNotice,
} from './assistant-consent';
import { AssistantComposer } from './assistant-composer';
import { AssistantThreadView } from './assistant-thread-view';
import { decodeOrgConsent } from './assistant-decode';
import { useAssistantInsertionTarget } from './assistant-insertion-target';
import {
  activeSurface,
  testIdPrefix,
  withOrgName,
  type AssistantPanelConfig,
} from './assistant-panel-config';
import {
  readNoticeDismissed,
  useAcceptOrgAssistantConsent,
  useAssistantConsentGate,
  useDismissNotice,
} from './use-assistant-consent';
import { sendContextKey, useAssistantThread, type ThreadMessage } from './use-assistant-thread';

export interface AssistantPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: AssistantPanelConfig;
  /**
   * Sit BESIDE the page (a wide screen) rather than over it: a full-height column under the console's
   * top bar, whose width the shell reserves (`AssistantPanelFrame`). Off, it is the right-hand sheet.
   */
  docked?: boolean;
  /** Docked only: the person opened it, so focus moves to the composer (`AssistantPanelFrame`). */
  focusOnOpen?: boolean;
}

/** Hold the host's emit behind a ref so an inline-built config cannot re-fire a mount signal on
 *  every render. Analytics must follow what happened, not how often the host re-rendered. */
function useStableEmit(emit: AssistantAnalyticsEmit | undefined): AssistantAnalyticsEmit {
  const ref = useRef(emit);
  ref.current = emit;
  return useCallback((event) => ref.current?.(event), []);
}

export function AssistantPanel({
  open,
  onOpenChange,
  config,
  docked = false,
  focusOnOpen = false,
}: AssistantPanelProps) {
  const prefix = testIdPrefix(config);
  const surface = activeSurface(config);
  const insertionTarget = useAssistantInsertionTarget();
  const emit = useStableEmit(config.emit);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [draft, setDraft] = useState('');

  // Gated on `open` like the thread reads: this panel is mounted for the whole session by the shell,
  // and a consent GET fired at boot on every load — one that 404s in today's world — is chatter an
  // operator reading the logs cannot attribute to anything a person did.
  const gate = useAssistantConsentGate(open ? config.orgRef : null);
  const accept = useAcceptOrgAssistantConsent(config.orgRef);
  const dismissNotice = useDismissNotice(config.noticeStorageKey);

  const thread = useAssistantThread({
    wireSurface: config.wireSurface,
    pageWireSurface: config.pageWireSurface,
    entryContextExtra: config.entryContextExtra,
    threadMatch: config.threadMatch,
    apiBaseUrl: config.apiBaseUrl,
    authHeaders: config.authHeaders,
    renderServerOpeningMessage: config.renderServerOpeningMessage,
    emit,
    active: open,
  });
  const { clearConsentRefusal, consentRefusal, replySendContexts } = thread;

  // The context a send from THIS page would stamp. A reply this panel asked for under a different
  // one (another page's register, another fundraiser's scope) is not offered to this page's field:
  // the server never held it to this page's rules. A reply of unknown origin (history, a
  // re-attached turn) keeps the page-and-position rule alone.
  const pageSendContext = sendContextKey(
    config.pageWireSurface ?? config.wireSurface,
    config.entryContextExtra,
  );
  const canInsertReply = useCallback(
    (message: ThreadMessage) => {
      const askedUnder = replySendContexts.get(message.key);
      return askedUnder === undefined || askedUnder === pageSendContext;
    },
    [pageSendContext, replySendContexts],
  );

  // The refusal's own state wins over the read: it was resolved at the moment of the refusal, with no
  // cache in between, so a revocation that landed since the panel opened is reflected here first.
  const refusedInstrument = useMemo(
    () => (consentRefusal === null ? null : decodeOrgConsent(consentRefusal.state)),
    [consentRefusal],
  );
  const instrument = refusedInstrument ?? gate.instrument;
  const hasInstrument = instrument !== null;
  const blocked = consentRefusal !== null || (!gate.pending && !gate.errored && !gate.satisfied);
  const canResolve = config.canAcceptForOrg && hasInstrument;

  // "Not now" is a real answer and it has to STICK for this visit — an effect that re-raised the
  // card the instant it closed would make declining impossible.
  const [declined, setDeclined] = useState(false);
  // Asked of someone who can answer it: on open when the read already says the organization has not
  // accepted, and on a refusal (which carries fresher state than any read). Everyone else gets the
  // honest line inside the panel and no interruption.
  const showCard = open && blocked && canResolve && !declined;

  // ⚠ RE-READ WHEN THE KEY RESOLVES. The key is per-identity, and this panel mounts with the shell —
  // before the host's own identity read has settled — so at first render the key is `null` and the
  // initializer can only answer "not dismissed". Without the re-sync the stored `true` is never
  // consulted and the once-per-person notice reappears on EVERY app load, which is the one thing a
  // once-notice must not do. The write-through on dismiss stays; this only reconciles.
  const [noticeDismissed, setNoticeDismissed] = useState(() =>
    readNoticeDismissed(config.noticeStorageKey),
  );
  const noticeStorageKey = config.noticeStorageKey;
  useEffect(() => {
    if (noticeStorageKey === null || noticeStorageKey.length === 0) return;
    if (readNoticeDismissed(noticeStorageKey)) setNoticeDismissed(true);
  }, [noticeStorageKey]);
  const showNotice = open && !showCard && !blocked && hasInstrument && !noticeDismissed;

  const handleDismissNotice = useCallback(() => {
    dismissNotice();
    setNoticeDismissed(true);
  }, [dismissNotice]);

  const handleAccept = useCallback(
    (contentHash: string) => {
      accept.mutate(contentHash, {
        onSuccess: () => {
          // The read is re-seeded by the mutation, so `blocked` clears and the panel opens onto the
          // question the person came to ask.
          clearConsentRefusal();
          emit({ name: ASSISTANT_EVENTS.consentGranted });
        },
      });
    },
    [accept, clearConsentRefusal, emit],
  );

  const handleCardOpenChange = useCallback(
    (next: boolean) => {
      if (next) return;
      setDeclined(true);
      // Declining changes nothing and closes the assistant entirely — the portal is exactly as it
      // was. It is recorded because "how many organizations were asked and said not now" is worth
      // knowing, and it carries no parameters: never who, never what they were about to ask.
      emit({ name: ASSISTANT_EVENTS.consentDeclined });
      clearConsentRefusal();
      onOpenChange(false);
    },
    [clearConsentRefusal, emit, onOpenChange],
  );

  useEffect(() => {
    if (consentRefusal === null) return;
    // A refusal means the person tried to use it after declining. Ask again — the answer they gave
    // was to the offer, not to the feature.
    setDeclined(false);
  }, [consentRefusal]);

  useEffect(() => {
    if (!open) return;
    emit({ name: ASSISTANT_EVENTS.opened, params: { entry_point: 'top_bar' } });
  }, [open, emit]);

  const handleSend = useCallback(() => {
    const text = draft;
    setDraft('');
    thread.send(text);
  }, [draft, thread]);

  const blockContext: BlockRenderContext = useMemo(
    () => ({ env: config.env, emit }),
    [config.env, emit],
  );

  // Follow the newest message. A person is looking at what just arrived, not at what they asked ten
  // minutes ago, so the thread sits against the composer and older turns run off the top edge.
  const endRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [thread.messages, thread.streamingText]);

  const policyLink = useMemo(
    () =>
      config.vocab.policyLinkPhrase === null || config.privacyPolicyUrl.length === 0
        ? null
        : { phrase: config.vocab.policyLinkPhrase, href: config.privacyPolicyUrl },
    [config.vocab.policyLinkPhrase, config.privacyPolicyUrl],
  );

  const hasThread = thread.messages.length > 0;
  const showThread = hasThread || thread.status !== 'idle';

  return (
    <>
      <AssistantPanelFrame
        open={open && !showCard}
        onOpenChange={onOpenChange}
        docked={docked}
        title={config.vocab.productName}
        testId={prefix}
        initialFocus={inputRef}
        focusOnOpen={focusOnOpen}
      >
        <Sheet.Body
          className={showThread ? 'flex flex-col justify-end' : 'flex flex-col'}
          testId={`${prefix}-body`}
        >
          {showNotice && instrument !== null && (
            <AssistantFirstUseNotice
              instrument={instrument}
              orgName={config.orgName}
              vocab={config.vocab}
              policyLink={policyLink}
              onDismiss={handleDismissNotice}
              testId={`${prefix}-notice`}
            />
          )}

          {/* Blocked, with nobody here who can unblock it. Two different sentences for two
                different causes — see `AssistantBlockedLine`. */}
          {blocked && !canResolve && (
            <AssistantBlockedLine
              message={withOrgName(
                hasInstrument ? config.vocab.consentBlockedLine : config.vocab.unavailableLine,
                config.orgName,
              )}
              testId={`${prefix}-blocked`}
            />
          )}

          {showThread ? (
            <AssistantThreadView
              messages={thread.messages}
              status={thread.status}
              streamingText={thread.streamingText}
              onRetry={thread.retry}
              insertionTarget={insertionTarget}
              canInsertReply={canInsertReply}
              vocab={config.vocab}
              blockContext={blockContext}
              testId={`${prefix}-thread`}
            />
          ) : (
            // `historyPending` is what keeps a returning admin from seeing "ask me something"
            // before their own thread paints — the empty state is an ANSWER ("you have not asked
            // anything"), and showing it while the answer is still unknown states a fact this
            // component has not learned yet.
            !blocked &&
            !thread.historyPending && (
              <EmptyState
                className="flex-1"
                icon={<Icon icon={Sparkles} size={20} aria-hidden />}
                title={config.vocab.emptyTitle}
                description={config.vocab.emptyBody}
                testId={`${prefix}-empty`}
              />
            )
          )}
          <div ref={endRef} aria-hidden {...testIdProp(`${prefix}-thread-end`)} />
        </Sheet.Body>

        {/* The composer takes the Footer as a COLUMN — chips over input — rather than the region's
              default trailing action row. Its border and padding recipe is untouched. */}
        <Sheet.Footer className="flex-col items-stretch justify-start gap-0">
          <AssistantComposer
            inputRef={inputRef}
            value={draft}
            onChange={setDraft}
            onSend={handleSend}
            onStop={thread.stop}
            streaming={thread.status === 'streaming'}
            surface={surface}
            disabled={blocked}
            vocab={config.vocab}
            testId={`${prefix}-composer`}
          />
        </Sheet.Footer>
      </AssistantPanelFrame>

      {instrument !== null && (
        <AssistantConsentCard
          open={showCard}
          onOpenChange={handleCardOpenChange}
          instrument={instrument}
          orgName={config.orgName}
          vocab={config.vocab}
          policyLink={policyLink}
          onAccept={handleAccept}
          accepting={accept.isPending}
          testId={`${prefix}-consent`}
        />
      )}
    </>
  );
}
